import {Injectable} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {BehaviorSubject, catchError, combineLatest, delayWhen, distinctUntilChanged, filter, forkJoin, interval, map, Observable, of, Subject, Subscription, switchMap, take, takeUntil, tap} from 'rxjs';
import {Application} from 'src/portaljs/constants/application';
import {MessageType} from 'src/portaljs/constants/message-type';
import {Message} from 'src/portaljs/models/message';
import {NavInfo} from 'src/portaljs/models/nav-info';
import {UserLoginModel} from '../models/user-login.model';
import {AuthService} from './auth.service';
import {ClientStateService} from './client-state.service';
import {EnvironmentService} from './environment.service';
import * as _ from 'lodash-es';
import Icons from '../enums/icons.enum';
import {MenuItem} from 'src/portaljs/models/menu-item';
import {SelectedMenu} from '../models/selected-menu.model';
import {ClientLoginDialogComponent} from 'src/app/features/client-login-dialog/client-login-dialog.component';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {ClientLoginService} from './client-login.service';
import {SessionService} from './session.service';
import Scope from '../enums/scopes.enum';
import {PortalApp} from '../shared/portal-app';
import {ConsoleLogger} from '../shared/console-logger';
import {UserStateEvent} from 'src/portaljs/models/user-state-event';
import {UserEvent} from 'src/portaljs/constants/user-event';
import {FakeGrants} from 'src/app/features/dev-only/testdata/fakeGrants';
import {PortalMessagingService} from './portal-messaging.service';
import {AuthenticationEvent} from '../models/authentication-event.model';
import AuthType from '../enums/auth.type.enum';
import {MessageCode, UserMessage} from '../shared/error/user-message';
import {ToastMessageService} from './toast-message.service';
import {DialogModeType} from '../shared/error/dialog-message-type.enum';
import {AppAction} from 'src/portaljs/models/app-action';
import LoginPrompt from '../enums/login-prompt.enum';
import {ErrorDialogService} from './error-dialog.service';
import {ErrorDisplayData} from '../models/error-display-data.model';
import {ChannelService} from './channel.service';
import {ChannelEvent, ChannelEventType} from '../models/channel-event.model';
import {DestinyViewType} from 'src/portaljs/constants/destiny-view-type';
import {Util} from '../shared/util';
import {GrantsModel, GrantsPermissions} from '../models/grants.model';
import {AppInfo} from '../models/app-info.model';
import {ThemeModel} from '../models/theme.model';
import PopupState from '../enums/popup.state.enum';
import {ReminderDialogService} from './reminder-dialog.service';
import {UserProfileSelectedItems} from '../models/user-profile-selected-items';
import {ThemeService} from './theme.service';
import {SecondaryToolbar} from 'src/portaljs/models/secondary-toolbar';
import {SidenavMenuStates} from '../enums/sidenav-menu-states.enum';
import {ScreenStateService} from './screen-state.service';
import {UnifiedSettingsService} from './unified-settings.service';
import {UnifiedSettingsUserModel} from '../models/unified-settings.model';
import {IframeService} from './iframe.service';
import {PortalAppAction} from '../enums/portal-app-action.enum';
import {LoginTokenModel} from '../models/login.token.model';
import {DebugMessage} from '../models/portal-debug-message';
import ImsRole from '../enums/ims-role.enum';

@Injectable({ providedIn: 'root' })
export class PortalStateService {
  private DESTINY_AI_CUSTOMER_NUMBERS: Record<string, string[]> = {
    'local':  [],    // []
    'dly':    [],    // []
    'qat':    [],    // [Napa, DNA Enriched Reports]
    'prd':    []
  };

  private pendo: any = window.pendo;
  private grantsErrorCount = 0;
  private heartbeatInterval: Subscription;
  private showSidenav: Record<Application, boolean> = {} as any;
  private lastSelectedApp: Application = Application.PORTAL;
  private defaultTitle: string = 'Follett Destiny';

  private _selectedMenus = new BehaviorSubject<SelectedMenu>(null);
  private selectedMenu$ = this._selectedMenus.asObservable();

  private _activeApp: BehaviorSubject<PortalApp> = new BehaviorSubject<PortalApp>(new PortalApp(Application.PORTAL, undefined, false, Icons.PALETTE));
  private activeApp$: Observable<PortalApp> = this._activeApp.asObservable().pipe(distinctUntilChanged());

  private _activeMenuItems: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>([]);
  private activeMenuItems$: Observable<MenuItem[]> = this._activeMenuItems.asObservable();

  private _activeSecondaryToolbar: BehaviorSubject<SecondaryToolbar> = new BehaviorSubject<SecondaryToolbar>(undefined);
  private activeSecondaryToolbar$: Observable<SecondaryToolbar> = this._activeSecondaryToolbar.asObservable();

  private _selectableApps: BehaviorSubject<PortalApp[]> = new BehaviorSubject<PortalApp[]>([]);
  private selectableApps$: Observable<PortalApp[]> = this._selectableApps.asObservable();

  private _showLoadingSpinner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private showLoadingSpinner$: Observable<boolean> = this._showLoadingSpinner.asObservable();

  private _userProfileSelection: BehaviorSubject<UserProfileSelectedItems> = new BehaviorSubject<UserProfileSelectedItems>({isUserProfileSelected: true, isInterestSurveySelected: false});
  private userProfileSelection$: Observable<UserProfileSelectedItems> = this._userProfileSelection.asObservable();

  public clickFromAppMenu:boolean = false;

  private _menuStateForApp: BehaviorSubject<Record<Application, {current: SidenavMenuStates, default: SidenavMenuStates}>>
    = new BehaviorSubject<Record<Application, {current: SidenavMenuStates, default: SidenavMenuStates}>>
    ({[Application.PORTAL]: {current: SidenavMenuStates.HIDDEN, default: SidenavMenuStates.HIDDEN}} as any);
  private menuStateForApp$: Observable<Record<Application, {current: SidenavMenuStates, default: SidenavMenuStates}>> = this._menuStateForApp.asObservable();

  private _isLoginProcessComplete: BehaviorSubject<boolean> =  new BehaviorSubject<boolean>(false);
  private isLoginProcessComplete$ = combineLatest([this.authService.appsFramed$, this.authService.appsSelected$])
    .pipe(
      map(([framed, selected]) => {
        ConsoleLogger.debug('portalStateService[1]: login process status => framed: ' + framed + ', selected: ' + selected);
        this._isLoginProcessComplete.next((framed && selected));
        return this._isLoginProcessComplete.value;
      })
    );

  private lastSelectedDescendantOfExpandableMenuItems: Record<string, string> = {};


  /**
   * Tracks whether the sidecar is currently minimized.
   *
   * This defaults to `true` so the service starts in the safest, least-assumptive
   * state before any sidecar UI has been opened or restored by the user. Until the
   * application explicitly reports that the sidecar is expanded, portal logic should
   * treat it as minimized.
   *
   * The value is used by activity/session management logic to determine whether
   * sidecar interactions should count as active user engagement. Because of that,
   * incorrect state can cause activity to be over-counted or missed, which in turn
   * can affect session timeout behavior.
   */
  private _isSidecarMinimized = new BehaviorSubject<boolean>(true);
  public isSidecarMinimized$ = this._isSidecarMinimized.asObservable();

  /**
   * Updates the cached sidecar minimized state.
   *
   * Call this when the sidecar UI changes between minimized and expanded states,
   * such as after user-driven open, restore, minimize, or close actions, or when
   * equivalent application events programmatically change the sidecar visibility.
   *
   * This setter does not perform side effects by itself; it keeps the shared state
   * accurate so any user activity tracking that depends on sidecar visibility can
   * correctly participate in session timeout decisions.
   *
   * @param isMinimized `true` when the sidecar is minimized/hidden; otherwise `false`.
   */
  public setSidecarMinimized(isMinimized: boolean): void {
    if (isMinimized === this._isSidecarMinimized.value) return; //no change, short-circuit
    ConsoleLogger.debug(`Setting sidecar minimized state to ${isMinimized}`);
    this._isSidecarMinimized.next(isMinimized);
  }

  constructor(private router: Router,
              private authService: AuthService,
              private messagingService: PortalMessagingService,
              private envService: EnvironmentService,
              private clientStateService: ClientStateService,
              private clientLoginService: ClientLoginService,
              private sessionService: SessionService,
              public dialog: MatDialog,
              public activatedRoute: ActivatedRoute,
              private toastService: ToastMessageService,
              private errorDialogService: ErrorDialogService,
              private channelService: ChannelService,
              private reminderDialogService: ReminderDialogService,
              private themeService: ThemeService,
              private screenStateService: ScreenStateService,
              private unsService: UnifiedSettingsService,
              private iframeService: IframeService,
              private title: Title) {

    this.isLoginProcessComplete$.subscribe();

    this.startHeartbeatInterval();

    // Listen for message received by the Portal and handle them...
    this.messagingService.getMessagesRcvd$()
      .subscribe((msg: Message) => this.handleIncomingMessage(msg));

    // Listen for user login/logout events...
    this.authService.getUserLogin$()
      .pipe(
        filter(u => !!u),
        switchMap((ulm: UserLoginModel) => {
          this.initializePendo(ulm);
          const themes$: Observable<ThemeModel> = this.authService.updateTheme(ulm);
          const apps$ = this.updateAvailableApps(ulm);
          return forkJoin([themes$, apps$]);
        })
      )
      .subscribe();

    this.handleRedeemedToken().subscribe();
    this.handleAuthenticationEvent();
    this.handleClientLoginPopupClosed();
    this.handleRemoveUser();
  }

  public isUnEnabledForCurrentApp() {
    let enabled = this.envService.isFUN_ENABLED();
    if (enabled && this.getActiveApp()?.app !== Application.PORTAL) {
      const appEnabled = this.showSidenav[this.getActiveApp()?.app];
      enabled = (appEnabled === undefined || appEnabled === null) ? enabled : appEnabled;
    }
    return enabled;
  }

  public setSideNavEnabledForCurrentApp(enabled: boolean) {
    this.showSidenav[this.getActiveApp()?.app] = enabled;
  }

  public getMenuStateForApp(app: Application = this.getActiveApp().app) {
    return this._menuStateForApp.value[app]?.current || this._menuStateForApp.value[app]?.default;
  }

  public getDefaultMenuStateForApp(app: Application = this.getActiveApp().app) {
    return this._menuStateForApp.value[app]?.default;
  }

  public getMenuState$() {
    return this.menuStateForApp$;
  }

  public setMenuStateForApp(value: SidenavMenuStates, app: Application = this.getActiveApp().app) {
    const currentState = this._menuStateForApp.value;
    const updatedState = {current: value, default: currentState[app]?.default};
    currentState[app] = updatedState;
    this._menuStateForApp.next(currentState);
  }

  public initializePendo(ulm: UserLoginModel) {
    if ( this.pendo && typeof this.pendo !== 'undefined' && this.envService.isProductionBuild()) {
      const userId: string = ulm.getSub();
      this.pendo.initialize({
        visitor: {
          id: userId
        }
      });
    }
  }
  public startHeartbeatInterval() {
    this.stopHeartbeatInterval();
    this.heartbeatInterval = interval(this.envService.getHeartbeatFrequency())
      .subscribe(() => this.sendHeartbeat());
  }

  public stopHeartbeatInterval() {
    if (this.heartbeatInterval) {
      this.heartbeatInterval.unsubscribe();
      this.heartbeatInterval = undefined;
    }
  }

  public getIsDestinyViewSelectorEnabled(): boolean {
    const activeApp = this.getActiveApp();
    return activeApp?.app === Application.DESTINY && this.clientStateService.getDestinyViewTypes().length > 0;
  }

  private handleRemoveUser() {
    this.authService.getRemoveUser$()
      .pipe(
        filter((edm) => !!edm),
        switchMap((edm: ErrorDisplayData) => {
          ConsoleLogger.error('Unexpected error, user removed -- code: ' + edm.messageCode + ', message: ' + edm.message, edm.error);
          this.channelService.sendChannelEvent(new ChannelEvent(ChannelEventType.LOGOUT));
          // return this.logoutAndNotify(edm);
          this.logoutAndNotify(edm).subscribe();
          return of (edm);
        })
      )
      .subscribe((edm) => {
        this.authService.setUserLogin(undefined); // OK to immediately destroy all existing embedded iframes
        this.stopHeartbeatInterval();
        this.errorDialogService.open( DialogModeType.ERROR, edm.message, edm.messageCode, undefined, JSON.stringify(edm.error), true, false, true );
      });
  }

  private logoutAndNotify(edm: ErrorDisplayData): Observable<ErrorDisplayData>{
    return this.authService.logout('logoutAndNotify')
      .pipe(
        catchError((err) => {
          ConsoleLogger.error('logout error', err);
          return of(undefined);
        }),
        switchMap(() => {
          this.resetQueryParamsWithoutTriggeringObservables();
          // Send logout message to each app and wait for up to 3 seconds before completely destroying all embedded frames and opening the error dialog
          return this.notifyAppsOfLogoutEvent(new AuthenticationEvent(undefined, AuthType.LOGOUT, undefined, 'loan2'), this.authService.getUserLogin())
            .pipe( map(() => edm) );
        })
      );
  }

  // Set the app, appId, siteGuid query params and clear the others query params without triggering any queryParam observables
  private resetQueryParamsWithoutTriggeringObservables() {
    const app = this.activatedRoute.snapshot.queryParamMap.get('app');
    const appId = this.activatedRoute.snapshot.queryParamMap.get('appId') || this.authService.getDistrictAppId();
    const siteGuid = this.activatedRoute.snapshot.queryParamMap.get('siteGuid') || this.authService.getSiteGuid();

    const queryParams = new URLSearchParams();
    if (!_.isEmpty(appId)) {
      queryParams.set('appId', appId || '');
    }
    if (!_.isEmpty(siteGuid)) {
      queryParams.set('siteGuid', siteGuid);
    }
    if (!_.isEmpty(app)) {
      queryParams.set('app', app);
    }
    const newUrl = window.location.pathname + '?' + queryParams.toString();
    window.history.replaceState({}, '', newUrl);
  }


  private handleClientLoginPopupClosed() {
    this.clientLoginService.getPopupState$()
      .subscribe((state: PopupState) => {
        if (state === PopupState.CLOSED) {
          this.setShowLoadingSpinner(false);
          this.setEmbeddedFramesAccessible(true);
        }
      });
  }

  private sendUserStateChangeMessages(userEvent: UserEvent, app: Application, authCode: string) {
    const pa = this.iframeService.findEmbeddedOrOverlayFrame(app);
    this.messagingService.sendUserStateChangeMessages(userEvent, pa, authCode, this.iframeService.continuationState[app]);
    delete this.iframeService.continuationState[app];
  }


  private sendLogoutMessages() {
    const embeddedAndOverlayApps = this.iframeService.getEmbeddedAndOverlayFrames();
    for (const app of embeddedAndOverlayApps) {
      this.iframeService.removeFromNewlyAddedEmbeddedFrameApps(app.app);
      const msgId = this.messagingService.getNextMessageId();
      const userState: UserStateEvent = {event: UserEvent.LOGOUT};
      const msg = new Message(msgId, MessageType.userStateChange, Application.PORTAL, app.app, userState);
      this.postMessage(msg);
      app.setReadyResponseReceived(false);
    }
    if (this.envService.isMessageDebuggingEnabled()) {
      this.envService.processDebugMessageQueue().pipe(take(1)).subscribe();
    }
  }


  private updateToast(app: Application, message: any) {
      this.toastService.show(message.type || DialogModeType.INFO, message.message, message.showDismiss, message.duration);
  }

  private updateAddressBarNavInfo(app: Application, navInfo: string | NavInfo) {
    const isNavInfoObject = !_.isString(navInfo) && navInfo != null;
    const nav = _.isString(navInfo) ? navInfo : navInfo?.nav;
    const pageTitle = _.isString(navInfo) ? undefined : navInfo?.pageTitle;

    const isActiveApp = this.getActiveApp().app === app;

    if (!_.isEmpty(pageTitle?.trim())) {
      this.clientStateService.setPageTitle(app, pageTitle);
      if (isActiveApp) {
        this.title.setTitle(pageTitle);
        const iframeElem: HTMLIFrameElement = this.iframeService.getIframeElement(app);
        if(iframeElem){
          iframeElem.title = pageTitle;
        }
      }
    }

    if (isActiveApp) {

      const queryParams: Params = { app: app, appId: this.authService.getDistrictAppId(), siteGuid: this.authService.getSiteGuid()};
      if (!_.isEmpty(nav?.trim())) {
        queryParams['nav'] = nav;

      } else if (isNavInfoObject && nav !== null) {
        // if the nav value came in on the NavInfo object and it wasn't null or a true value, maintain the existing nav value from the url
        const currentNav = this.activatedRoute.snapshot.queryParamMap.get('nav');
        if (currentNav !== null) {
          queryParams['nav'] = currentNav;
        }
      }
      ConsoleLogger.debug('portalStateService[2]: nav 0');
      return this.router.navigate([], {relativeTo: this.activatedRoute, queryParams});
    }
    return of(undefined);
  }

  private shouldShowSidecarForApp(app?: Application): boolean {
    return app === Application.DESTINY
      || app === Application.DASHBOARD
      || app === Application.DESTINY_UI;
  }

  private handleUserActivityDetected(msg: Message) {
    if (msg.source === Application.JUNIPER_SIDECAR) {
      if (!this._isSidecarMinimized.value) {
        const activeApp = this.getActiveApp()?.app;
        if (this.shouldShowSidecarForApp(activeApp)) {
          this.sessionService.updateUserSessionExpirationTime(msg.source);
          this.reminderDialogService.close();
        }
      }
      return;
    }
    if (msg.source === this.getActiveApp().app) {
      this.sessionService.updateUserSessionExpirationTime(msg.source);
      this.reminderDialogService.close();
    }
  }

  public handleReadyMessage(app: Application, isReady: boolean, clearOldMessages: boolean) {
    this.iframeService.setReadyResponseReceived(app, true);

    if (clearOldMessages) {
      this.clientStateService.removeUnackMessages(app);
    }

    if (isReady) {
      if (app === Application.DASHBOARD && this.getActiveApp()?.app === Application.DASHBOARD) {
        this.sendActivate(this.getSelectableApp(app));
      }

      if (app === Application.JUNIPER_SIDECAR) {
        this.sendSidecarLifecycleMessage(this.getActiveApp()?.app);
      }

      this.clientStateService.getUnackMessages$()
        .pipe(
          take(1),
          map((msgs: Message[]) => _.filter(msgs, (m: Message) => m.destination === app))
        )
        .subscribe((messages: Message[]) => {
          for (const msg of messages) {
            this.postMessage(msg);
          }
        });
    }

  }

  public getSelectableApp(app: Application): PortalApp {
    if (app === Application.PORTAL) {
      return new PortalApp(Application.PORTAL, undefined, false, Icons.PALETTE);
    }
    return this.getSelectableApps().find(f => f.app === app);
  }
  public getSelectableApps(): PortalApp[] {
    return this._selectableApps.value ?? [];
  }
  public getSelectableApps$(): Observable<PortalApp[]> {
    return this.selectableApps$
      .pipe(
        map((portalApps: PortalApp[] = []) => Util.orderPortalApps(portalApps))
      );
  }
  private setSelectableApps(selectableApps: PortalApp[] = []) {
    if (selectableApps) {
      // De-dupe and order the list of apps alphabetically
      selectableApps = _.uniqBy(selectableApps, 'app');
      selectableApps.sort((f1: PortalApp, f2: PortalApp) => {
        if (f1.app < f2.app) return -1;
        if (f1.app > f2.app) return 1;
        return 0;
      });
    }
    return this._selectableApps.next(selectableApps);
  }

  public setEmbeddedFramesAccessible(isAccessible: boolean = true) {
    for (const f of this.iframeService.getEmbeddedFrames()) {
      if (this.getSelectableApps().some(s => s.app === f.app)) {
        f.accessible = isAccessible;
      }
    }
  }



  public getSelectedMenu$(): Observable<SelectedMenu> {
    return this.selectedMenu$;
  }
  public getSelectedMenuId$(activeApp: Application): Observable<string> {
    return this.selectedMenu$
      .pipe(
        map(menuItems => menuItems ? menuItems[activeApp]?.menuItemId : undefined)
      );
  }
  public getSelectedMenuId(activeApp: Application): string {
    const selMenu: SelectedMenu = this._selectedMenus.getValue();
    if (!_.isEmpty(selMenu)) {
      return selMenu[activeApp]?.menuItemId;
    }
    return undefined;
  }

  public setSelectedMenuId(selectedItem: SelectedMenu) {
    const selectedMenus = this._selectedMenus.value;
    this._selectedMenus.next({ ...selectedMenus, ...selectedItem });
  }

  public setActiveAppFromApplication(app: Application): Promise<void> {
    const deepLinkAppAction = this.generateAppActionFromDeepLink();
    const continuationStateAppAction = this.generateAppActionFromContinuationState(app);
    if (deepLinkAppAction) {
      ConsoleLogger.debug('portalStateService[3].setActiveAppFromApplication: setActiveAppFromApplication: deepLinkAppAction => app: ' + app + ', action: ' + JSON.stringify(deepLinkAppAction));
      return this.setActiveApp(this.getSelectableApp(deepLinkAppAction.destination), deepLinkAppAction);
    } else if (continuationStateAppAction) {
      ConsoleLogger.debug('portalStateService[4]' + ': setActiveAppFromApplication: continuationStateAppAction => app: ' + app + 'action: ' + JSON.stringify(continuationStateAppAction));
      return this.setActiveApp(this.getSelectableApp(continuationStateAppAction.destination), continuationStateAppAction);
    } else {
      const pa = this.getSelectableApp(app);
      ConsoleLogger.debug('portalStateService[5]' + ': setActiveAppFromApplication: => app: ' + app + ', pa: ' + pa?.app);
      return this.setActiveApp(pa);
    }
  }

  public async setActiveApp(newApp: PortalApp, appAction?: AppAction, activatedByAppAction?: Application): Promise<void> {
    this.lastSelectedApp = newApp?.app;
    newApp = this.iframeService.findEmbeddedFrame(newApp?.app) ?? newApp; // !!! Always use the existing embeddedFrame instance if it exists, because state is set on embeddedFrames !!!

    return new Promise((resolve) => {
      if (newApp?.app === undefined || newApp?.isExternal) {
        resolve();
      } else {
        const spinnerTimeout = setTimeout(() => {
          if (!this.iframeService.isReadyResponseReceived(newApp)) {
            this.setShowLoadingSpinner(true);
          }
        }, 750);


        /** If app has a parent, and the parent has not yet loaded, ensure the parent app is loaded before trying to load the child. */
        let appLoad$;
        if (newApp.parent && !this.iframeService.isReadyResponseReceived(this.iframeService.findEmbeddedFrame(newApp.parent))) {
          const parentApp = this.getSelectableApp(newApp.parent);
          appLoad$ = this.iframeService.addToEmbeddedFrames(parentApp)
            .pipe(
              switchMap(() => {
                return this.iframeService.addToEmbeddedFrames(newApp);
              })
            );
        } else {
          appLoad$ = this.iframeService.addToEmbeddedFrames(newApp);
        }

        appLoad$
          .pipe(
            take(1),
            catchError((err) => {
              ConsoleLogger.error('Error adding embeddedFrames', err);
              this.setShowLoadingSpinner(false);
              clearTimeout(spinnerTimeout);
              this.setShowLoadingSpinner(false);
              resolve();
              return of(undefined);
            })
          )
          .subscribe(() => {
            if (!this.iframeService.isReadyResponseReceived(newApp)) {
              const start = new Date().getTime();
              const intv = setInterval(() => {
                if (this.iframeService.isReadyResponseReceived(newApp)) {
                  this.activateAppWhenAppIsReady(newApp, appAction, activatedByAppAction);
                  clearTimeout(spinnerTimeout);
                  clearInterval(intv);
                  this.setShowLoadingSpinner(false);
                  resolve();
                } else if ((new Date().getTime() - start) > this.envService.getLogoutTimeout()) {
                  clearTimeout(spinnerTimeout);
                  clearInterval(intv); // Give up after 10 seconds
                  this.setShowLoadingSpinner(false);
                  resolve();
                }
              }, 500);
            } else {
              this.activateAppWhenAppIsReady(newApp, appAction, activatedByAppAction);
              clearTimeout(spinnerTimeout);
              this.setShowLoadingSpinner(false);
              resolve();
            }
          });
      }
    });
  }


  private activateAppWhenAppIsReady(newApp: PortalApp, appAction?: AppAction, activatedByAppAction?: Application): boolean {

    const isNewAppReady = this.iframeService.isReadyResponseReceived(newApp);
    if (isNewAppReady) {
      const currApp = this.getActiveApp();

      // This is an appAction triggered by the currently active app. Allow the app to re-activate itself even if it is not the last selected app.
      if (this.lastSelectedApp === activatedByAppAction && currApp?.app === activatedByAppAction) {
        this.lastSelectedApp = newApp.app;
      }

      if ( newApp.app === this.lastSelectedApp ) {
        if (currApp && newApp && newApp.app !== currApp.app) {
          this.sendActivate(newApp, activatedByAppAction !== undefined);
          this.sendPassivate(currApp);
          this._activeApp.next(newApp);
          this.sendSidecarLifecycleMessage(newApp.app);
          this.unsService.updateLastApp(this.authService.getUserLogin(), newApp.app).subscribe();
        }
        this.setActiveMenuItems();
        this.setActiveSecondaryToolbar();
        this.setActiveTitle();

        const menuState = this.screenStateService.isMobile() ? SidenavMenuStates.HIDDEN : this.getMenuStateForApp(newApp.app);
        this.setMenuStateForApp(menuState);

        if (appAction) {
          this.sendAppAction(appAction);
        } else {
          this.sendAppActionIfNeeded(newApp);
        }
        return true;
      }
    }
    return false;
  }


  public getActiveApp$(): Observable<PortalApp> {
    return this.activeApp$;
  }
  public getActiveApp(): PortalApp {
    return this._activeApp.value;
  }

  public initializeAllMenuItems() {
    this.clientStateService.setMenuItems(Object.values(Application), []);
    for (const app of Object.values(Application)) {
      this.setSelectedMenuId({ [app]: undefined });
    }
    this.setActiveMenuItems();
  }
  public setActiveMenuItems() {
    this._activeMenuItems.next(this.clientStateService.getMenuItems(this.getActiveApp()?.app));
  }
  public getActiveMenuItems$(): Observable<MenuItem[]> {
    return this.activeMenuItems$;
  }
  public get activeMenuItems(): MenuItem[] {
    return this._activeMenuItems.getValue();
  }

  public initializeSecondaryToolbars() {
    this.clientStateService.setSecondaryToolbar(Object.values(Application), {breadcrumbItems: [], buttonItems: [], selectItems: []});
    this.setActiveSecondaryToolbar();
  }
  public setActiveSecondaryToolbar() {
    this._activeSecondaryToolbar.next(this.clientStateService.getSecondaryToolbar(this.getActiveApp()?.app));
  }
  public setActiveTitle() {
    const pageTitle = this.clientStateService.getPageTitle(this.getActiveApp()?.app);
    this.title.setTitle(!_.isEmpty(pageTitle?.trim()) ? pageTitle : this.defaultTitle);
  }
  public getActiveSecondaryToolbar$(): Observable<SecondaryToolbar> {
    return this.activeSecondaryToolbar$;
  }
  public getActiveSecondaryToolbar(): SecondaryToolbar {
    return this._activeSecondaryToolbar.getValue();
  }

  public handleIncomingMessage(msg: Message) {
    if (msg) {
      const mtype = msg.id ? msg.type + '[' + msg.id + ']' : msg.type;
      ConsoleLogger.debug('Received \'' + mtype + '\' from [' + msg.source + ']: ' + JSON.stringify(msg.message));
    }

    if (this.envService.isMessageDebuggingEnabled()) {
      const userGuid = this.authService.getUserLogin()?.getUserGuid() || '';
      const params = this.authService.getDebugParams();
      this.envService.addDebugMessage(userGuid, new DebugMessage(msg.id, msg.source, msg.destination, msg.type, msg.message, params));
    }


    if (msg && msg.destination === Application.PORTAL) {
      switch (msg.type) {
        case MessageType.updateNavInfo:
          this.updateAddressBarNavInfo(msg.source, msg.message);
          break;
        case MessageType.toast:
          this.updateToast(msg.source, msg.message);
          break;
        case MessageType.iframeMetadata:
          this.iframeService.updateIframeMetadata(msg.source, msg.message);
          break;
        case MessageType.userActivityDetected:
          this.handleUserActivityDetected(msg);
          break;
        case MessageType.ready: // Application is ready to communicate w/ the Portal
          this.handleReadyMessage(msg.source, msg.message.isReady, msg.message.clearUnackedMessages);
          break;
        case MessageType._auto_ack: // Auto-acks by the PortalJS lib
          this.clientStateService.removeUnackMessage(msg);
          break;
        case MessageType.createDeepLink:
          this.handleDeepLink(msg);
          break;
        case MessageType.destinyViewType:
          this.handleDestinyViewTypes(msg);
          break;
        case MessageType.registerSecondaryToolbar:
          if (this.envService.isFUN_ENABLED()) {
            this.clientStateService.setSecondaryToolbar([msg.source], msg.message);
            this.setActiveSecondaryToolbar();
          }
          break;
        case MessageType.updateSecondaryToolbarBreadcrumbs:
        case MessageType.updateSecondaryToolbarButtons:
        case MessageType.updateSecondaryToolbarSelects:
          if (this.envService.isFUN_ENABLED()) {
            this.clientStateService.updateSecondaryToolbarItems(msg.source, msg.type, msg.message);
            this.setActiveSecondaryToolbar();
          }
          break;
        case MessageType.registerMenuItems: {
          const currentApp = this.iframeService.findEmbeddedFrame(msg.source) || new PortalApp(msg.source, undefined, false, undefined);
          if (currentApp.parent) {
            break; // Don't update menu items for child applications...
          }

          const children = currentApp?.children || [];
          const childrenPlusParent = [...children, currentApp.app];

          if (this.envService.isFUN_ENABLED()) {
            if (msg.message.options) {
              for (const childApp of childrenPlusParent) {
                this.showSidenav[childApp] = msg.message.options?.position === 'left';
                const size = msg.message.options?.size || SidenavMenuStates.SMALL_ICONS;
                const currStateForApp = this.getMenuStateForApp(childApp);
                let currentState: SidenavMenuStates;
                if (this.screenStateService.isMobile()) {
                  currentState = SidenavMenuStates.HIDDEN;
                } else {
                  currentState = !currStateForApp || currStateForApp === SidenavMenuStates.HIDDEN ? size : currStateForApp;
                }

                const menuState = {
                  default: size,
                  current: currentState
                };
                this._menuStateForApp.next({...this._menuStateForApp.value, [childApp]: menuState});
              }
            }
          }

          // Child apps get the same menu items as the parent app
          this.clientStateService.setMenuItems(childrenPlusParent, msg.message.menuItems || msg.message);

          this.setActiveMenuItems();
          break;
        }
        case MessageType.login: {
          const dialogConfig = new MatDialogConfig();
          dialogConfig.disableClose = false;
          dialogConfig.panelClass = 'login-panel';

          if (msg.message === 'nav_failed_login') {
            if (this.authService.getUserLogin()?.isGuestUser()) {
              const appUrl = this.iframeService.findEmbeddedFrame(msg.source)?.url;
              if (appUrl) {
                const url = new URL(appUrl);
                const nav = url.searchParams.get('nav');
                if (nav) {
                  this.iframeService.continuationState[msg.source] = AuthService.CONTINUATION_STATE_NAV_TOKEN + AuthService.stripNavToken(nav) + msg.message as any;
                }
              }
            }
          } else {
            this.iframeService.continuationState[msg.source] = msg.message as any;
          }

          dialogConfig.data = { loginRequestedBy: msg.source };
          this.dialog.open(ClientLoginDialogComponent, dialogConfig);
          break;
        }
        case MessageType.notification:
          this.clientStateService.setNotifications(msg.source, msg.message);
          break;
        case MessageType.appAction:
          this.handleAppAction(msg);
          break;
        case MessageType.redeemToken:
          ConsoleLogger.error('Unexpected redeemToken message received from ' + msg.source + ', ignoring.');
          break;
        case MessageType.activateMenuItem:
          this.handActivateMenuItem(msg);
          break;
        default:
          ConsoleLogger.error( 'Unknown message type received: ' + JSON.stringify(msg) );
          break;
      }
    }
  }


  private handleDestinyViewTypes(msg: Message) {
    if (msg?.message) {
      this.clientStateService.setDestinyViewTypeVO(msg.message);
    }
  }


  private handleDeepLink(msg: Message) {
    if (!msg || !msg.source || !msg.message) {
      ConsoleLogger.error(`Unable to generate link for invalid message: ${msg?.message} `);
    } else {
      try {
        const deepLinkUrl = this.envService.getUrl(Application.PORTAL) + '/portal'
          + '?app=' + encodeURIComponent(msg.source)
          + '&appId=' + this.authService.getDistrictAppId()
          + '&siteGuid=' + this.authService.getSiteGuid()
          + '&link=' + this.generateDeepLinkFromAction(msg.message);

        this.postMessage(new Message( msg.id, MessageType.getPageUrl, Application.PORTAL, msg.source, deepLinkUrl));
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        ConsoleLogger.error(`Unable to generate link for source: ${msg.message.source}, route: ${msg.message.route}, params: ${msg.message.params} `);
      }
    }
  }


  private handActivateMenuItem(msg: Message) {
    const currentApp: Application = this.getActiveApp().app;
    if (currentApp === msg.source) {
      this.setSelectedMenuId( {[currentApp]: {menuItemId: msg.message, setByApp: true}} );
    }
  }


  private handleAppAction(msg: Message) {
    let skipAppAction:boolean = false;
    this.handleUserProfileAction(msg);

    if (msg.source === Application.UNIFIED_SETTINGS) {
      if( !(msg?.message?.route) || msg?.message?.route === ''){
        skipAppAction = true;
      }
      if (msg?.message?.route === PortalAppAction.DistrictSettingsUpdate) {
        this.unsService.fetchUnifiedDistrictSettings(this.authService.getDistrictAppId()).subscribe();
      } else if (msg?.message?.route === PortalAppAction.UserPreferencesUpdate) {
        const uns: UnifiedSettingsUserModel = this.unsService.getUnifiedSettings();
        if (uns) {
          uns.setTrackingLastApp(!!msg.message.params?.trackingLastApp);
          uns.setTrackingLastSite(!!msg.message.params?.trackingLastSite);
          this.unsService.setUnifiedSettings(uns);
          return undefined;
        } else {
          return this.unsService.fetchPortalUserSetting(this.authService.getUserLogin())
            .pipe(take(1))
            .subscribe((uns: UnifiedSettingsUserModel) => {
              uns.setTrackingLastApp(!!msg.message.params?.trackingLastApp);
              uns.setTrackingLastSite(!!msg.message.params?.trackingLastSite);
              this.unsService.setUnifiedSettings(uns);
            });
        }
      }
    }

    const app = this.getSelectableApp(msg.message.destination);

    if(skipAppAction){
      this.setActiveApp(app, undefined, msg.source);
    } else {
      this.setActiveApp(app, msg.message, msg.source);

      /**
       * Sending the message is being handled in 'activateAppWhenAppIsReady()'
       */
      // if (msg?.message?.route || msg?.message?.params) {
      //   const destMessage: Message = new Message(undefined, MessageType.appAction, Application.PORTAL, app.app, msg.message);
      //   this.postMessage(destMessage);
      // }
    }
    return undefined;
  }


  /**
   * Receiving an AppAction from DD or Collections indicates the user is either
   * closing Collection's UserProfile or closing DD's InterestSurvey
   */
  private handleUserProfileAction(msg: Message) {
    if (this.isInterestSurveySelected() && msg.source === Application.DD) {
      this.setInterestSurveySelected(false);
    }

    if (this.isUserProfileSelected() && msg.source === Application.COLLECTIONS) {
      this.setUserProfileSelected(false);
    }
  }


  private updateAvailableApps(ulm: UserLoginModel): Observable<PortalApp[]> {
    if (this.envService.isUsingFakeGrants()) {
      return of(this.handleGrants(new FakeGrants(), ulm));
    }

    // Call Destiny via backend to get list of available apps
    return this.authService.fetchGrants(ulm)
      .pipe(
        map((grants: any) => {
          this.grantsErrorCount = 0;
          return this.handleGrants(grants, ulm);
        }),
        catchError(error => {
          ConsoleLogger.error('Failed to get grants: ' + error?.status);

          if (error?.status === 401) {
            this.grantsErrorCount++;

            if (this.grantsErrorCount >= 3) {
              this.grantsErrorCount = 0;
              this.authService.logFailInfo(undefined, {fn: 'updateAvailableApps', loc: 'fetchGrants', msg: '3x'}, error).pipe(take(1)).subscribe();
              this.authService.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.genericFailureMsg, JSON.stringify(error), MessageCode.ERROR_GRANTS_FAILED));
              return of(undefined);
            }

            // A 401 indicates that our access token may have expired.
            ConsoleLogger.error('Failed to get grants: attempting token refresh');
            return this.authService.refreshToken(5000, 1) // Retry refresh on failure one time after 5 seconds
              .pipe(
                map((tokens: LoginTokenModel): PortalApp[] => {
                  if (tokens) {
                    const ulm: UserLoginModel = new UserLoginModel( this.authService.getClaimsFromToken(tokens.idToken), tokens );
                    this.authService.setUserLogin(ulm); // This will trigger a re-fetch of grants, which will call this function again with an incremented grantsErrorCount
                  } else {
                    this.grantsErrorCount = 0;
                    ConsoleLogger.error('Unexpected lack of tokens when refreshing token after failing to fetch grants');
                    this.authService.logFailInfo(undefined, {fn: 'updateAvailableApps', loc: 'refreshNoTokens'}, error).pipe(take(1)).subscribe();
                    this.authService.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.genericFailureMsg, JSON.stringify(error), MessageCode.ERROR_GRANTS_FAILED));
                  }
                  return undefined;
                }),
                catchError((err) => { // If we can't get a refresh, we're done.
                  this.grantsErrorCount = 0;
                  ConsoleLogger.error('Unexpected error refreshing token after failing to fetch grants: ' + JSON.stringify(err));
                  this.authService.logFailInfo(undefined, {fn: 'updateAvailableApps', loc: 'refresh'}, error).pipe(take(1)).subscribe();
                  this.authService.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.genericFailureMsg, JSON.stringify(error), MessageCode.ERROR_GRANTS_FAILED));
                  return of(undefined);
                })
              );
          } else {
            this.grantsErrorCount = 0;
            ConsoleLogger.error('Unexpected error fetching Grants: ' + JSON.stringify(error));
            this.authService.logFailInfo(undefined, {fn: 'updateAvailableApps', loc: 'fetchGrants', msg: 'non401'}, error).pipe(take(1)).subscribe();
            this.authService.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.genericFailureMsg, JSON.stringify(error), MessageCode.ERROR_GRANTS_FAILED));
            return of(undefined);
          }
        })
      );
  }


  private handleGrants(grants: any, ulm: UserLoginModel): PortalApp[] {
    let embeddedFrames: PortalApp[] = [];
    if (grants) {
      if (this.envService.isUsingFakeGrants()) {
        grants = new FakeGrants();
      }

      this.authService.startAccessExpCheck(grants.role?.sessionTimeoutMillis);

      const scopes: Scope[] = grants.scopes;
      if (scopes) {
        let portalApp: PortalApp;
        const appSwitcherApps: PortalApp[] = [];

        // Add Dashboard
        if(this.hasDashboardAccess(grants)) {
          const url = this.envService.getUrl(Application.DASHBOARD);
          if(url) {
            portalApp = new PortalApp(Application.DASHBOARD, url, false, Icons.DASHBOARD);
            embeddedFrames.push(portalApp);
            appSwitcherApps.push(portalApp);
          }
        }

        // Add HelpDesk
        if(this.hasHelpDeskAccess(grants) && ulm.isUSCustomer()) {
            portalApp = new PortalApp(Application.MASTERLIBRARY_HELPDESK, undefined, true, Icons.HELP_DESK);
            embeddedFrames.push(portalApp);
            appSwitcherApps.push(portalApp);
        }

        // Add Fundraising Manager
        if(this.hasFundraisingAccess(grants)) {
          const url = this.envService.getUrl(Application.FM);
          if(url) {
            portalApp = new PortalApp(Application.FM, url, true, Icons.FUNDRAISING);
            embeddedFrames.push(portalApp);
            appSwitcherApps.push(portalApp);
          }
        }

        // D-38323: If DD is not avaiable, still need a Collections frame so user can select 'Profile'. This frame will be an internal only view of Collections
        // S-70796: Temporarly back out the D-38323 change. For now, only show Profile if they have access to DD
        // if (!_.some(scopes, s => s === Scope.DISCOVER_ACCESS)) {
        //   portalApp = new PortalApp(Application.COLLECTIONS, undefined, false, Icons.COLLECTIONS);
        //   portalApp.internal = true;
        //   framedApps.push(portalApp);
        //   appSwitcherApps.push(portalApp);
        // }


        // Add Classroom Library Manager
        if (this.hasClassroomLibraryAccess(grants)) {
          const url = this.envService.getUrl(Application.CLM);
          if(url) {
            portalApp = new PortalApp(Application.CLM, url, false, Icons.CLM);
            embeddedFrames.push(portalApp);
            appSwitcherApps.push(portalApp);
          }
        }

        // Add Unified Settings
        if( this.envService.isFUN_ENABLED() && ulm.doesUserHaveAccessToSettings() ) {
          const url = this.envService.getUrl(Application.UNIFIED_SETTINGS);
          if(url) {
            portalApp = new PortalApp(Application.UNIFIED_SETTINGS, url, false, Icons.USER_PROFILE);
            portalApp.internal = true;
            embeddedFrames.push(portalApp);
            appSwitcherApps.push(portalApp);
          }
        }

        for (const scope of scopes) {
          switch (scope) {
            case Scope.DESTINY_BACKOFFICE_ADMIN:
            case Scope.DESTINY_BACKOFFICE_ACCESS:

              if (!this.envService.isAppHidden(Application.PSARI)) {
                if (this.isPsariAvailable()) { // Portal SubApp Reference Implementation...Should only be for testing...
                  const url = this.envService.getUrl(Application.PSARI);
                  portalApp = new PortalApp(Application.PSARI, url, false, Icons.CHECKMARK);
                  portalApp.internal = true;
                  embeddedFrames.push(portalApp);
                  appSwitcherApps.push(portalApp);
                }
              }

              if (!this.envService.isAppHidden(Application.DESTINY_UI)) {
                if (this.isDestinyUiAvailable()) {
                  const url = this.envService.getUrl(Application.DESTINY_UI);
                  portalApp = new PortalApp(Application.DESTINY_UI, url, false, Icons.CHECKMARK);
                  portalApp.internal = true;
                  embeddedFrames.push(portalApp);
                  appSwitcherApps.push(portalApp);
                }
              }


              if (!this.envService.isAppHidden(Application.DESTINY)) {
                // Add LibraryManager except for Shared User
                if(!ulm.isSharedUser()) {
                  portalApp = new PortalApp(Application.DESTINY, undefined, false, Icons.DESTINY, this.isPsariAvailable(), this.isDestinyUiAvailable());
                  embeddedFrames.push(portalApp); // Always push Destiny onto the framedApps, even if no library permissions. ResourceManager will reference.
                  appSwitcherApps.push(portalApp);
                }
              }
              break;
            case Scope.DISCOVER_ACCESS:
              // Add DD
              if (!this.envService.isAppHidden(Application.DD) && this.envService.getUrl(Application.DD)) {
                portalApp = new PortalApp(Application.DD, undefined, false, Icons.DD);
                embeddedFrames.push(portalApp);
                appSwitcherApps.push(portalApp);
              }

              // Add Collections
                if (!this.envService.isAppHidden(Application.COLLECTIONS) && this.envService.getUrl(Application.COLLECTIONS)) {
                  portalApp = new PortalApp(Application.COLLECTIONS, undefined, false, Icons.COLLECTIONS);
                  portalApp.internal = !ulm.isCollectionsEnabled();

                  embeddedFrames.push(portalApp);
                  appSwitcherApps.push(portalApp);
                }
              break;
            case Scope.DISCOVER_ADMIN:
              if (!ulm.isDestinyAdmin()) { // TEMPORARY: Hide from DestinyAdmin until DAP can properly handle DestinyAdmin.
                if (this.hasFollettShelfId(ulm) && !this.envService.isAppHidden(Application.DISCOVER_MANAGER)) {
                  // Add FSSAdmin
                  portalApp = new PortalApp(Application.DISCOVER_MANAGER, undefined, true, Icons.DISCOVER_MANAGER);
                  embeddedFrames.push(portalApp);
                  appSwitcherApps.push(portalApp);
                }
              }
              break;
            case Scope.TITLEWAVE_ACCESS:
              // Add Titlewave
              if (!this.envService.isAppHidden(Application.TITLEWAVE)) {
                portalApp = new PortalApp(Application.TITLEWAVE, undefined, true, Icons.TITLEWAVE);
                embeddedFrames.push(portalApp);
                appSwitcherApps.push(portalApp);
              }
              break;
            case Scope.COMMUNITY_ACCESS:
              // Add Community
              if (!this.envService.isAppHidden(Application.FOLLETT_COMMUNITY)) {
                portalApp = new PortalApp(Application.FOLLETT_COMMUNITY, this.envService.getUrl(Application.FOLLETT_COMMUNITY), true, Icons.FOLLETT_COMMUNITY);
                embeddedFrames.push(portalApp);
                appSwitcherApps.push(portalApp);
              }
              break;
            case Scope.ONLINE_STORE_ACCESS:
              // Add Destiny Express
              if (!this.envService.isAppHidden(Application.DESTINY_EXPRESS)) {
                portalApp = new PortalApp(Application.DESTINY_EXPRESS, this.envService.getUrl(Application.DESTINY_EXPRESS), true, Icons.DESTINY_EXPRESS);
                embeddedFrames.push(portalApp);
                appSwitcherApps.push(portalApp);
              }
              break;
            case Scope.MASTERLIBRARY_WORKORDERS:
              if (ulm.isUSCustomer() && !this.envService.isAppHidden(Application.MASTERLIBRARY_WORKORDERS)) {
                portalApp = new PortalApp(Application.MASTERLIBRARY_WORKORDERS, undefined, true, Icons.MASTERLIBRARY_WORKORDERS);
                embeddedFrames.push(portalApp);
                appSwitcherApps.push(portalApp);
              }
              break;
            case Scope.MASTERLIBRARY_DRAWINGS:
              if (ulm.isUSCustomer() && !this.envService.isAppHidden(Application.MASTERLIBRARY_DRAWINGS)) {
                portalApp = new PortalApp(Application.MASTERLIBRARY_DRAWINGS, undefined, true, Icons.MASTERLIBRARY_DRAWINGS);
                embeddedFrames.push(portalApp);
                appSwitcherApps.push(portalApp);
              }
              break;
            case Scope.MASTERLIBRARY_SCHEDULES:
              if (ulm.isUSCustomer() && !this.envService.isAppHidden(Application.MASTERLIBRARY_SCHEDULES)) {
                portalApp = new PortalApp(Application.MASTERLIBRARY_SCHEDULES, undefined, true, Icons.MASTERLIBRARY_SCHEDULES);
                embeddedFrames.push(portalApp);
                appSwitcherApps.push(portalApp);
              }
              break;
            case Scope.MASTERLIBRARY_ITAM:
              if (ulm.isUSCustomer() && !this.envService.isAppHidden(Application.MASTERLIBRARY_ITAM)) {
                portalApp = new PortalApp(Application.MASTERLIBRARY_ITAM, undefined, true, Icons.MASTERLIBRARY_WORKORDERS);
                embeddedFrames.push(portalApp);
                appSwitcherApps.push(portalApp);
              }
              break;
            default:
              break;
          }
        }

        if( !_.some(appSwitcherApps, { app: Application.MASTERLIBRARY_ITAM })){
          const itamEnabled: boolean = this.unsService.isITAMEnabled();
          if(itamEnabled && ulm.isUSCustomer() && ulm.hasRole([ImsRole.Administrator, ImsRole.Staff, ImsRole.Faculty])
            && !this.envService.isAppHidden(Application.MASTERLIBRARY_ITAM)){
            portalApp = new PortalApp(Application.MASTERLIBRARY_ITAM, undefined, true, Icons.MASTERLIBRARY_WORKORDERS);
            embeddedFrames.push(portalApp);
            appSwitcherApps.push(portalApp);
          }
        }

        if (!this.envService.isAppHidden(Application.DESTINY_AI) && this.isDestinyAiAvailableForUser(ulm, grants)) {
          portalApp = new PortalApp(Application.DESTINY_AI, undefined, false, null);
          embeddedFrames.push(portalApp);
          appSwitcherApps.push(portalApp);
        }

        // if (!_.some(scopes, s => s === Scope.DISCOVER_ACCESS)) {
        if(ulm.isSharedUser()) {
          // Add DD and Collections
          if (!_.some(embeddedFrames, s => s.app === Application.DD)) {
            if (!this.envService.isAppHidden(Application.DD) && this.envService.getUrl(Application.DD)) {
              portalApp = new PortalApp(Application.DD, undefined, false, Icons.DD);
              embeddedFrames.push(portalApp);
              appSwitcherApps.push(portalApp);
            }
          }

          if (!_.some(embeddedFrames, s => s.app === Application.COLLECTIONS)) {
            if (!this.envService.isAppHidden(Application.COLLECTIONS) && this.envService.getUrl(Application.COLLECTIONS)) {
              portalApp = new PortalApp(Application.COLLECTIONS, undefined, false, Icons.COLLECTIONS);
              portalApp.internal = !ulm.isCollectionsEnabled();

              embeddedFrames.push(portalApp);
              appSwitcherApps.push(portalApp);
            }
          }
        }

        if (this.authService.isDestinyForcePasswordReset()) {
          ConsoleLogger.debug('isDestinyForcePasswordReset: true');
          const allowedApps: PortalApp[] = appSwitcherApps.filter(s => s.app === Application.DESTINY || s.isExternal)
                                                          .filter(s => s.app !== Application.DISCOVER_MANAGER) // External LTI launch
                                                          .filter(s => s.app !== Application.FM); //  External LTI launch
          this.setSelectableApps(allowedApps);
          this.authService.setAppsSelected(true);

          embeddedFrames = _.uniqBy(embeddedFrames, 'app');
          this.iframeService.processEmbeddedFrames(embeddedFrames.filter(f => f.app === Application.DESTINY), false)
            .pipe(
              map((removedApps: PortalApp[]) => {
                this.handleRemovedApps(removedApps);
                return removedApps;
              }),
              take(1)
            )
            .subscribe(() => {
              this.authService.setAppsFramed(true);
              this.handleDestinyInsecurePasswordForceReset();
              this.processOverlayFrames(grants);
            });
        } else {
          this.setSelectableApps(appSwitcherApps);
          this.authService.setAppsSelected(true);

          embeddedFrames = _.uniqBy(embeddedFrames, 'app');
          this.iframeService.processEmbeddedFrames(embeddedFrames)
            .pipe(
              map((removedApps: PortalApp[]) => {
                this.handleRemovedApps(removedApps);
                return removedApps;
              }),
              take(1)
            )
            .subscribe(() => {
              this.authService.setAppsFramed(true);
              this.processOverlayFrames(grants);
            });
        }
        this.setShowLoadingSpinner(false);
      }
    }

    // Since updating grants triggers PortalComponent to update the initial page for the new user,
    // wait until we know what apps can be selected before calling setGrants()
    this.authService.setGrants(grants);
    return embeddedFrames;
  }

  private handleRemovedApps(removedApps: PortalApp[]) {
    for (const ra of removedApps) {
      this.clientStateService.setMenuItems([ra.app], []);
      this.setActiveMenuItems();
      this.clientStateService.setSecondaryToolbar([ra.app], {breadcrumbItems: [], buttonItems: [], selectItems: []});
      this.setActiveSecondaryToolbar();
    }
  }


  private processOverlayFrames(grants: GrantsModel) {
    // This is fire and forget. We don't need to wait for overlays to be processed before continuing.
    this.iframeService.processOverlayFrames(grants)
      .pipe(
          catchError((err) => {
          ConsoleLogger.error('Error processing overlay frames, but continuing', err);
          return of(undefined);
        }),
        take(1)
      )
      .subscribe();
  }


  public hasFundraisingAccess(grants?: GrantsModel): boolean {
    const scopes: Scope[] = grants?.scopes || [];
    return scopes.indexOf(Scope.FUNDRAISING_MANAGER) >= 0;
  }

  public hasClassroomLibraryAccess(grants: GrantsModel) {
    return (!!grants?.portalPermissions?.clmActive || (!this.envService.isProductionBuild() && localStorage.getItem('showClm') === 'true'));
  }

  private isPsariAvailable(): boolean {
    return !this.envService.isPrd();
  }
  private isDestinyUiAvailable(): boolean {
    return this.envService.isDestinyUIEnabled();
  }

  public isDestinyAiAvailableForUser(ulm: UserLoginModel = this.authService.getUserLogin(), grants: GrantsModel = this.authService.getGrants()): boolean {
    return this.isApplicationAvailableForGrantsWithUser(Application.DESTINY_AI, grants, ulm);
  }

  /** This controls whether Destiny AI should show in the app switcher.
   * DestinyAI should only show for DestinyAdmins, at both the site and at the district.
   * However, DestinyAI does not exist at site level. When selected at the site level, we need to navigate to the district first then switch to it.
   * This method only verifies DestinyAI is enabled for the customer and that the user is a Destinyadmin, regardless of at the site or district.
   *
   * isDestinyAiAvailable() will consider the destinyAdmin's current location (site vs district) and only return true if they are at the district level.
   * This will prevent DestinyAI from being framed when the user is at a site.
   */
  public shouldShowDestinyAiInAppPicker(ulm: UserLoginModel = this.authService.getUserLogin(), scopes: Scope[] = []): boolean {

    if (!scopes || scopes.length === 0) {
      scopes = this.authService.getGrants()?.scopes || [];
    }
    const destinyAiCustomerNumbers = this.getDestinyAiCustomerNumbersForEnv();
    if (destinyAiCustomerNumbers.length === 0 || destinyAiCustomerNumbers.indexOf(ulm.getDistrictCustomerNumber()) >= 0) {
      if (this.isApplicationAvailableForScopes(Application.DESTINY_AI, scopes)) {
        return !!ulm.isDestinyAdmin();
      }
    }

    return false;
  }

  public hasDashboardAccess(grants: GrantsModel) {
    if( this.authService.isDestinyAdmin()) {
      return false;
    }
    const scopes: Scope[] = grants.scopes;
    const isDistrictUser = this.authService.isDistrictUserNotAtSite();
    const libDashboard = scopes && scopes.indexOf(Scope.LIBRARY_ACCESS)  >= 0 && (grants.permissions.indexOf(GrantsPermissions.DASHBOARD_SHOW_LIBRARY) >= 0 || isDistrictUser);
    //resource has specific DashboardShowResouce checkbox for district user
    const resourceDashboard = scopes && scopes.indexOf(Scope.RESOURCE_ACCESS)  >= 0 && grants.permissions.indexOf(GrantsPermissions.DASHBOARD_SHOW_RESOURCE) >= 0 ;
    return libDashboard || resourceDashboard;
  }


  public hasHelpDeskAccess(grants?: GrantsModel) {
    const scopes = grants?.scopes || [];
    return scopes.indexOf(Scope.MASTERLIBRARY_HELPDESK) >= 0;
  }

  public isApplicationAvailableForGrantsWithUser(application: Application, grants: GrantsModel, ulm: UserLoginModel) {
    if (application === Application.CLM) {
      return this.hasClassroomLibraryAccess(grants);
    } else if (application === Application.DASHBOARD) {
      return this.hasDashboardAccess(grants);
    } else if (application === Application.MASTERLIBRARY_HELPDESK) {
      return this.hasHelpDeskAccess(grants);
    } else if (application === Application.FM) {
      return this.hasFundraisingAccess(grants);
    } else {
      return this.isApplicationAvailableForScopesWithUser(application, grants?.scopes, ulm);
    }
  }

  private isApplicationAvailableForScopesWithUser(application: Application, scopes: Scope[] = [], ulm: UserLoginModel) {
    if (application === Application.DESTINY_AI) {
      if (ulm.isDestinyAdminNotAtSite()) { // Destinyadmin at the site should not be able to access DestinyAI. They need to navigate to the district first.
        return this.shouldShowDestinyAiInAppPicker(ulm, scopes); // shouldShowDestinyAiInAppPicker() will validate the scopes and customer number
      }
    } else {
      if (this.isApplicationAvailableForScopes(application, scopes)) {
        if (application === Application.DESTINY || application === Application.PSARI || application === Application.DESTINY_UI) {
          return !ulm.isSharedUser();
        } else if (application === Application.COLLECTIONS) {
          return ulm.isCollectionsEnabled();
        } else if (application === Application.DISCOVER_MANAGER) {
          return !!ulm.getFollettShelfId();
        }
        return true;
      }
    }
    return false;
  }


  private isApplicationAvailableForScopes(application: Application, scopes: Scope[] = []) {
    let allowed = false;
    switch (application) {
      case Application.DESTINY:
      case Application.PSARI:
      case Application.DESTINY_UI: {
        const allowedScopes = [Scope.DESTINY_BACKOFFICE_ADMIN, Scope.DESTINY_BACKOFFICE_ACCESS];
        allowed = _.intersection(scopes, allowedScopes).length > 0;
        break;
      }
      case Application.DD:
      case Application.COLLECTIONS:
        allowed = scopes.indexOf(Scope.DISCOVER_ACCESS) >= 0;
        break;
      case Application.DISCOVER_MANAGER:
        allowed = scopes.indexOf(Scope.DISCOVER_ADMIN) >= 0;
        break;
      case Application.TITLEWAVE:
        allowed = scopes.indexOf(Scope.TITLEWAVE_ACCESS) >= 0;
        break;
      case Application.FOLLETT_COMMUNITY:
        allowed = scopes.indexOf(Scope.COMMUNITY_ACCESS) >= 0;
        break;
      case Application.DESTINY_EXPRESS:
        allowed = scopes.indexOf(Scope.ONLINE_STORE_ACCESS) >= 0;
        break;
      case Application.DESTINY_AI:
        allowed = scopes.indexOf(Scope.DESTINY_AI) >= 0;
        break;
      default:
        break;
    }
    return allowed;
  }

  public hasFollettShelfId(ulm: UserLoginModel) {
    return (this.authService.isDistrictUserNotAtSite() && !!ulm.getDistrictFollettShelfId()) || !!ulm.getFollettShelfId();
  }


  public sendHeartbeat(date = new Date()) {
    for (const app of this.iframeService.getEmbeddedAndOverlayFrames()) {
      if (this.iframeService.isReadyResponseReceived(app)) {
        const msgId = this.messagingService.getNextMessageId();
        const msg = new Message( msgId, MessageType.keepAlive, Application.PORTAL, app.app, date);
        this.postMessage(msg);
      } else {
        ConsoleLogger.debug('heartbeat NOT sent for: ' + JSON.stringify(app.app) + '. Not Ready');
      }
    }
  }


  private generateDeepLinkFromAction(appAction: AppAction): string {
    if (!appAction || !appAction.source) {
      throw new Error('Invalid appAction');
    }

    const AMP = encodeURIComponent('&');
    const EQ = encodeURIComponent('=');

    let paramString = 'source' + EQ + encodeURIComponent(appAction.source);

    if (appAction.route) {
      paramString += AMP + 'route' + EQ + encodeURIComponent(appAction.route);
    }

    if (appAction.params) {
      const keyvals = _.split(appAction.params, '&');
      for (const keyval of keyvals) {
        const pair = _.split(keyval, '=');
        const key = pair[0];
        const val = pair[1];
        if (val) {
          paramString += AMP + encodeURIComponent(key) + EQ + encodeURIComponent(val);
        }
      }
    }
    return paramString;
  }

  private generateAppActionFromContinuationState(app: Application): AppAction {
    let actionInfo: any;
    const continuationState = this.authService.temporary_continuation_state;
    this.authService.temporary_continuation_state = undefined;

    try {
        if (continuationState) {
          ConsoleLogger.debug('portalStateService[8]: continuationState being used to navigate: ' + continuationState);
          if (app === Application.DD && continuationState.indexOf('details') >= 0) {
            const cs = continuationState.slice(continuationState.indexOf('details'));
            const vals = _.split(cs, '/');
            if(vals?.length > 0 && vals[0] === 'details') {
              actionInfo = {source: Application.PORTAL, params: ''};
              actionInfo.route = 'deepdetail';
              actionInfo.params = 'bibid=titleBibId:' + vals[1];
              actionInfo.destination = app;
            }
          }
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch(error) {
        ConsoleLogger.error('Unable to process continuationState: ' + continuationState);
    }
    return actionInfo;
  }

  private generateAppActionFromDeepLink(): AppAction {
    let actionInfo: any;
    const deepLink = this.authService.temporary_deep_link;
    this.authService.temporary_deep_link = undefined;

    if (deepLink) {
      ConsoleLogger.debug('portalStateService[9]: deeplink being used to navigate: ' + deepLink);

      try {
        actionInfo = {source: Application.PORTAL, params: ''};

        const keyvals = _.split(deepLink, '&');
        for (const keyval of keyvals) {
          const pair = _.split(keyval, '=');
          const key = decodeURIComponent(pair[0]);
          const val = decodeURIComponent(pair[1]);

          if (key === 'route') {
            actionInfo.route = val;
          } else if (key === 'source') {
            actionInfo.destination = val;
          } else {
            if (actionInfo.params) {
              actionInfo.params += '&';
            }
            actionInfo.params += ( key + '=' + val );
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch(error) {
        ConsoleLogger.error('Unable to process link: ' + deepLink);
      }
    }

    return actionInfo;
  }


  public setActiveAppFromScopes(grants: GrantsModel) {
    const scopes: Scope[] = grants.scopes;
    const deepLinkAppAction = this.generateAppActionFromDeepLink();
    if (deepLinkAppAction) {
      const portalApp: PortalApp = this.getSelectableApp(deepLinkAppAction.destination);
      if (portalApp) {
        this.setActiveApp(portalApp, deepLinkAppAction);
      }
    } else {
      const prefApp = this.unsService.getUnifiedSettings()?.getPreferredApp();
      if (prefApp) {
        const portalApp: PortalApp = this.getSelectableApp(prefApp);
        if (portalApp) {
          this.setActiveApp(portalApp);
          return;
        }
      }


      // Fallback if not preferred app found
      if(scopes.indexOf(Scope.DESTINY_BACKOFFICE_ACCESS) >= 0 || scopes.indexOf(Scope.DESTINY_BACKOFFICE_ADMIN) >= 0) {
        if (this.authService.isDestinyAdminAtSite() ) {
          this.setActiveApp(new PortalApp(Application.DESTINY, this.envService.getUrl(Application.DESTINY), false, Icons.DESTINY, this.isPsariAvailable(), this.isDestinyUiAvailable()));
        } else if (scopes.indexOf(Scope.DISCOVER_ACCESS) >= 0 && grants.permissions?.indexOf(GrantsPermissions.DEFAULT_UD_SEARCH) >= 0) {
          this.setActiveApp(new PortalApp(Application.DD, this.envService.getUrl(Application.DD), false, Icons.DD));
        } else {
          this.setActiveApp(new PortalApp(Application.DESTINY, this.envService.getUrl(Application.DESTINY), false, Icons.DESTINY, this.isPsariAvailable(), this.isDestinyUiAvailable()));
        }
      } else {
        if (scopes.indexOf(Scope.DISCOVER_ACCESS) >= 0 ){
            this.setActiveApp(new PortalApp(Application.DD, this.envService.getUrl(Application.DD), false, Icons.DD));
        } else {
          if (this.iframeService.getEmbeddedFrames().length > 0) {
            this.setActiveApp(this.iframeService.getEmbeddedFrames()[0]);
          } else {
            ConsoleLogger.error('Invalid Scopes found. No valid application.');
          }
        }
      }
    }
  }

  public sendActivate(app: PortalApp, activatedByAppAction: boolean = false) {
    if (app && app.app !== Application.PORTAL) {
      const msgMessage: any = {'app': app.app};
      if(activatedByAppAction){
        msgMessage['activatedByAppAction'] = true;
      }
      const msg: Message = new Message( undefined, MessageType.activate, Application.PORTAL, app.app, msgMessage);
      this.postMessage(msg);
    }
  }

  public sendPassivate(app: PortalApp) {
    if (app && app.app !== Application.PORTAL) {
      this.iframeService.removeFromNewlyAddedEmbeddedFrameApps(app.app);
      const msg: Message = new Message( undefined, MessageType.passivate, Application.PORTAL, app.app, app.app );
      this.postMessage(msg);
    }
  }

  private sendSidecarLifecycleMessage(activeApp?: Application): void {
    if (!activeApp) { return; }
    const sidecarApp = this.iframeService.findOverlayFrame(Application.JUNIPER_SIDECAR);
    if (sidecarApp && this.iframeService.isReadyResponseReceived(sidecarApp)) {
      if (this.shouldShowSidecarForApp(activeApp)) {
        this.sendActivate(sidecarApp);
      } else {
        this.sendPassivate(sidecarApp);
      }
    }
  }

  public sendAppAction(appAction: AppAction) {
    if (appAction) {
      const msg: Message = new Message( undefined, MessageType.appAction, Application.PORTAL, appAction.destination, appAction);
      this.postMessage(msg);
    }
  }

  public sendAppActionIfNeeded(activeApp: PortalApp) {
    if (activeApp) {
      if (activeApp.app === Application.DESTINY) {
        if(this.authService.isDistrictUserNotAtSite()) {
          this.sendAppAction({destination: activeApp.app, source:Application.PORTAL, route: 'DistrictUserSite'});
        } else if(this.authService.isDestinyAdminNotAtSite()) {
          this.sendAppAction({destination: activeApp.app, source:Application.PORTAL, route: 'DestinyAdminSetup'});
        }
      }
    }
  }

  public sendDestinyViewTypeChange(viewType: DestinyViewType) {
    if (viewType) {
      const msg: Message = new Message( undefined, MessageType.destinyViewType, Application.PORTAL, Application.DESTINY, viewType );
      this.postMessage(msg);
    }
  }

  /**
   * PortalStateService.postMessage() will send messages and expect those messages to be acknowledged by the embedded app.
   * MessagingService.postMessage() just sends the message w/o an expectation of an acknowledgment.
   */
  public postMessage(msg: Message) {
    const destApp = this.iframeService.findEmbeddedFrame(msg.destination) || this.iframeService.getOverlayFrames().find(f => f.app === msg.destination);
    if (destApp) {
      this.messagingService.postAppMessage(destApp, msg);
    }
  }


  public getShowingLoadingSpinner$(): Observable<boolean> {
    return this.showLoadingSpinner$;
  }
  public setShowLoadingSpinner(showSpinner: boolean = false) {
    this._showLoadingSpinner.next(showSpinner);
  }


  public notifyAppsOfLogoutEvent(authEvent: AuthenticationEvent, ulm: UserLoginModel): Observable<UserLoginModel> {
    ConsoleLogger.debug('PortalStateService.notifyAppsOfAuthEvent() - authType: ' + authEvent.authType);
    this.clientStateService.clearNotifications();

    // Send logout messages to all apps (including overlays)
    this.sendLogoutMessages();

    // Don't wait for overlay apps, but make sure they are updated appropriately.
    this.iframeService.waitForAppsToBeReady(this.iframeService.getOverlayFrames(), [], this.iframeService.getOverlayFrames()).subscribe();

    // Wait for the apps to respond with a 'ready' message (don't wait for overlays to be ready)
    // If an app doesn't respond in time, mark app as unready
    return of(ulm)
      .pipe(
        delayWhen(() => this.iframeService.waitForAppsToBeReady(this.iframeService.getEmbeddedFrames(), [], this.iframeService.getEmbeddedFrames())),
        tap(() => this.iframeService.markAppsWithLoadingError(this.iframeService.getEmbeddedFrames()))
      );
  }



  /** ----------------------------------------------------------------------------------------- */
  /** Called *after* token is successfully redeemed */
  private handleRedeemedToken(): Observable<UserLoginModel> {
    return this.authService.getTokenRedeemed$()
      .pipe(
        filter(u => !!u),
        tap((ulm: UserLoginModel) => {
          this.setShowLoadingSpinner(true);
          this.authService.setUserLogin(ulm);
          this.startHeartbeatInterval();
        })
      );
  }

  private handleAuthenticationEvent() {
    this.authService.getAuthenticationEvent$()
      .pipe(filter(ae => !!ae))
      .subscribe((authEvent: AuthenticationEvent) => {
        ConsoleLogger.debug('PortalStateService.handleAuthenticationEvent() - event: ' + JSON.stringify(authEvent));
        this.lastSelectedDescendantOfExpandableMenuItems = {};

        this.iframeService.hideAllEmbeddedFrames();
        if (this.getActiveApp().app !== Application.PORTAL) {
          // Update the address bar to remove the nav query param.
          // The nav param should be updated by the app after it is activated.
          // Don't update on initial load of Portal.
          this.updateAddressBarNavInfo(this.getActiveApp().app, undefined);
        }

        if (authEvent.authType !== AuthType.LOGIN) {
          this.initializeAllMenuItems();
          this.initializeSecondaryToolbars();
        }

        if (authEvent.authType === AuthType.DISTUSER_NO_SITE_DESTINY_HACK) {
          const ai: AppInfo = this.envService.appInfos.find(a => a.name === Application.DESTINY);
          this.authService.getAppAuthCodes(ai.appId, 'distuser_no_site')
          .pipe(
            tap(authCode => {
              if (this.iframeService.getEmbeddedFrames().some(f => f.app === Application.DESTINY)) {
                this.sendUserStateChangeMessages(UserEvent.LOGIN, Application.DESTINY, authCode[ai.appId]);
              }
              this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.SITE_SWITCH, undefined, 'distuser_no_site'));
          })).subscribe();
        } else if (authEvent.authType === AuthType.SITE_SWITCH) {
            this.authService.switchSiteLogin(authEvent.siteGuid)
              .pipe(
                catchError((error) => {
                  ConsoleLogger.debug('Error switching site: ' + JSON.stringify(error));
                  return this.authService.logout('siteSwitchError')
                    .pipe(
                      switchMap(() => this.notifyAppsOfLogoutEvent(this.authService.getAuthenticationEvent(), this.authService.getUserLogin())),
                      switchMap(() => {
                        this.setActiveAppFromApplication(Application.PORTAL);
                        this.authService.setUserLogin(undefined); // OK to immediately destroy all existing embedded iframes
                        ConsoleLogger.debug('portalStateService[10]: nav 1');
                        this.router.navigate([], {
                          relativeTo: this.activatedRoute,
                          replaceUrl: true
                        });
                        return of(undefined);
                      })
                    );
                })
              )
              .subscribe(() => {
                ConsoleLogger.debug('Site switched successfully');
              });
        } else {
          this.authService.stopAccessExpCheck();

          if(authEvent.authType === AuthType.LOGOUT){
            if ( this.authService.isDistrictUserNotAtSite() || this.authService.isDestinyAdminNotAtSite() ) {
              ConsoleLogger.debug('handleAuthenticationEvent calling LOGOUT for District');
              this.authService.logout(authEvent.source)
                .pipe(
                  switchMap(() => this.notifyAppsOfLogoutEvent(this.authService.getAuthenticationEvent(), this.authService.getUserLogin()))
                )
                .subscribe(() => {
                  this.setActiveAppFromApplication(Application.PORTAL);
                  this.authService.setUserLogin(undefined); // OK to immediately destroy all existing embedded iframes
                  ConsoleLogger.debug('portalStateService[11]: nav 2');
                  this.router.navigate([], {
                    queryParams: {appId: this.authService.getDistrictAppId()},
                    relativeTo: this.activatedRoute,
                    replaceUrl: true
                  });
                });
            } else {
              ConsoleLogger.debug('handleAuthenticationEvent calling login() authType: ' + authEvent.authType);
              if(this.envService.isBrowserPopupBlocked()) {
                this.notifyAppsOfLogoutEvent(new AuthenticationEvent(undefined, AuthType.LOGOUT, undefined, 'fromEvent'), this.authService.getUserLogin())
                .pipe(take(1))
                .subscribe(() => {
                  this.authService.revokeAndLoginGuest(this.getActiveApp()?.app, authEvent.source);
                });
              } else {
                this.authService.revokeAndLoginGuest(undefined, authEvent.source);
              }
            }

          } else { // LOGIN
            ConsoleLogger.debug('handleAuthenticationEvent calling login() authType: ' + authEvent.authType);
            let st:string = undefined;
            if(this.envService.isBrowserPopupBlocked() && this.getActiveApp()?.app && this.iframeService.continuationState[this.getActiveApp().app]) {
              st = this.iframeService.continuationState[this.getActiveApp().app] as any;
              ConsoleLogger.debug('handleAuthenticationEvent browserPopupBlocked with continuationState: ' + st);
              delete this.iframeService.continuationState[this.getActiveApp().app];
            }
            this.authService.login({
              loginPrompt: authEvent.loginPrompt,
              fullscreen: false,
              app: this.getActiveApp()?.app,
              siteGuid: authEvent.siteGuid,
              continuationState: st
            }, authEvent.source);
          }
        }
      });
  }

  public getAppName(app: Application): string {
    let name: string = app;
    if (app === Application.DESTINY) {
      name = 'Back Office';
      if (!this.authService.getSiteGuid()) {
        name = this.authService.isDestinyAdmin() ? 'District Setup' : 'District ' + name;
      }
    } else if (app === Application.DASHBOARD) {
      if (!this.authService.getSiteGuid()){
        name = 'District ' + app;
      } else {
        name = 'Site ' + app;
      }
    } else if (app === Application.CLM) {
      name = 'Classroom Library';
    } else if (app === Application.DESTINY_UI) {
      name = 'Back Office';
      if (!this.authService.getSiteGuid()) {
        name = 'District ' + name;
      }
    }

    return name;
  }

  public getUserProfileSelection$(): Observable<UserProfileSelectedItems> {
    return this.userProfileSelection$;
  }
  public isUserProfileSelected(): boolean {
    return this._userProfileSelection.value.isUserProfileSelected;
  }
  public isInterestSurveySelected(): boolean {
    return this._userProfileSelection.value.isInterestSurveySelected;
  }
  public setUserProfileSelected(userProfileSelected: boolean) {
    const up: UserProfileSelectedItems = this._userProfileSelection.value;
    up.isUserProfileSelected = userProfileSelected;
    up.isInterestSurveySelected = false;
    this._userProfileSelection.next(up);
  }
  public setInterestSurveySelected(isInterestSurveyOpen: boolean) {
    const up: UserProfileSelectedItems = this._userProfileSelection.value;
    up.isInterestSurveySelected = isInterestSurveyOpen;
    up.isUserProfileSelected = false;
    this._userProfileSelection.next(up);
  }


  private handleDestinyInsecurePasswordForceReset() {
    if (this.authService.isDestinyForcePasswordReset()) {
      const msg: Message = {
        source: Application.PORTAL,
        destination: Application.DESTINY,
        message: {
          fss_reason: 'PortalPasswordNotSecure',
          siteGuid: this.authService.getUserLogin().getSiteGuid(),
          districtUser: this.authService.isDistrictUser() || this.authService.isDestinyAdmin()
        }
      } as any;

      // Acitvate Destiny and send AppAction, then clear sites to remove the site switcher
      this.initiateDestinyUserAccountChange(msg)
        .pipe(take(1))
        .subscribe();
    }
  }


  public initiateDestinyUserAccountChange(msg: Message) {
    if (msg?.message?.fss_reason) {
      const appAction: AppAction = {source: Application.PORTAL, destination: Application.DESTINY, route: msg.message.fss_reason};

      if (msg.message.districtUser !== undefined) {
        appAction.params = {districtUser: String(msg.message.districtUser)};
      }

      const alreadyLoadedDestiny = this.iframeService.findEmbeddedFrame(Application.DESTINY);
      const obs$ = alreadyLoadedDestiny ? this.iframeService.waitForApps([alreadyLoadedDestiny], [], this.iframeService.getEmbeddedFrames()) : of(undefined);
      return obs$
        .pipe(
          switchMap((removedApps: PortalApp[]) => {
            if (!removedApps || removedApps.find(a => a.app === Application.DESTINY)) {
              _.remove(this.iframeService.getEmbeddedFrames(), f => f.app === Application.DESTINY);
              return this.activateFakeDestinyAndSendUserAccountResetRequest(msg, appAction);
            } else {
              this.lastSelectedApp = Application.DESTINY;
              this.activateAppWhenAppIsReady(alreadyLoadedDestiny, appAction);

              return of(undefined);
            }
          })
        );
    }
    return of(undefined);
  }

  private activateFakeDestinyAndSendUserAccountResetRequest(msg: Message, appAction: AppAction) {
    let fakeDestiny = new PortalApp(Application.DESTINY, undefined, false, Icons.DESTINY, this.isPsariAvailable(), this.isDestinyUiAvailable());
    fakeDestiny.setReadyResponseReceived(false);
    let siteGuid = undefined;
    if (msg.message?.siteGuid) {
      siteGuid = msg.message?.siteGuid;
    }

    let fssReason = msg.message?.fss_reason;
    if (fssReason ==='PortalPasswordNotSecure') {
      fssReason = undefined;
      siteGuid = undefined;
    }

    return this.iframeService.addToEmbeddedFrames(fakeDestiny, siteGuid)
      .pipe(
        tap(() => {
          fakeDestiny = this.iframeService.findEmbeddedFrame(Application.DESTINY);
          this.lastSelectedApp = Application.DESTINY;
          this.activateAppWhenAppIsReady(fakeDestiny, appAction);
        })
      );
  }


  public waitForLoginProcessToComplete(): Observable<boolean> {
    return of(true)
      .pipe(
        delayWhen(() => {
          ConsoleLogger.debug('portalStateService[12]: waitForLoginProcessToComplete 0');
          const _timeExpired: Subject<boolean> = new Subject<boolean>();
          const timer = setTimeout(() => {
            ConsoleLogger.debug('portalStateService[13]: waitForLoginProcessToComplete() - timeout reached');
            _timeExpired.next(true);
            _timeExpired.complete();
          }, 10000);

          interval(100)
            .pipe( takeUntil(_timeExpired) )
            .subscribe(() => {
              if (this._isLoginProcessComplete.value) {
                ConsoleLogger.debug('portalStateService[14]: waitForLoginProcessToComplete() - complete!');
                clearTimeout(timer);
                _timeExpired.next(true);
                _timeExpired.complete();
              }
            });

          return _timeExpired.asObservable();
        })
      );
  }

  public sendSkipToContentAppAction() {
    const activeApp = this.getActiveApp();
    if(activeApp ){
      const appAction: AppAction = {source: Application.PORTAL, destination: activeApp.app, route: 'skipToMainContent'};
      this.sendAppAction(appAction);
    }
  }

  public async goToDistrictHomePage() {
    if (this.authService.getSiteGuid()) {
      const queryParams = {
        appId: this.authService.getUserLogin()?.getAppId(),
        forceHome: true
      };
      await this.setActiveAppFromApplication(Application.PORTAL);
      await this.router.navigate([], {queryParams}); // clean up query params
      this.authService.setSiteGuid(undefined);
      this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.SITE_SWITCH, undefined, 'gotoDistrictHome'));
    } else {
      await this.setActiveAppFromApplication(Application.PORTAL);
    }
  }



  /**
   * DestinyAI is only reachable at the DISTRICT by destinyadmin. If the destinyadmin is at a site, they must switch to district first.
   */
  public async goToDestinyAi() {
    if (this.authService.getUserLogin()?.isDestinyAdminNotAtSite()) {
      const pa: PortalApp = this.getSelectableApps().find(s => s.app === Application.DESTINY_AI);
      if (pa) {
        this.setActiveApp(pa, {destination: Application.DESTINY_AI, source: Application.PORTAL, route: 'DestinyAI'});
      } else {
        ConsoleLogger.error('Destiny AI app not available for this user.');
      }
    } else if (this.authService.getUserLogin()?.isDestinyAdminAtSite()) {
      const queryParams = {
        appId: this.authService.getUserLogin()?.getAppId(),
        dai: true
      };
      await this.setActiveAppFromApplication(Application.PORTAL);
      await this.router.navigate([], {queryParams}); // clean up query params
      this.authService.setSiteGuid(undefined);
      this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.SITE_SWITCH, undefined, 'gotoDestinyAi'));
    }
  }


  public setLastSelectedDescendantOfExpandableMenuItems(parentId: string, childId: string) {
    this.lastSelectedDescendantOfExpandableMenuItems[parentId] = childId;
  }
  public getLastSelectedDescendantOfExpandableMenuItems(parentId: string): string {
    return this.lastSelectedDescendantOfExpandableMenuItems[parentId];
  }

  private getDestinyAiCustomerNumbersForEnv(): string[] {
    const env = this.envService.getEnvironment().toLowerCase();
    return this.DESTINY_AI_CUSTOMER_NUMBERS[env] || [];
  }

}
